"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import { LazyMotion, domAnimation, m, useReducedMotion } from "framer-motion";
import { LocationContentBody } from "@/components/location/LocationContentBody.client";
import type { LocationContentVm } from "@/lib/locations/types";
const slideUp = {
  hidden: {
    y: 40,
    opacity: 0
  },
  visible: (i: number) => ({
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: [0.215, 0.61, 0.355, 1],
      delay: i * 0.1
    }
  })
} as const;
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
} as const;
export type LocationContentSectionClientProps = LocationContentVm;
export function LocationContentSectionClient(t0) {
  const $ = _c(20);
  const {
    heading,
    body,
    images
  } = t0;
  const prefersReduced = useReducedMotion();
  const variants = prefersReduced ? undefined : slideUp;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      once: true,
      amount: 0.2
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== heading || $[2] !== variants) {
    t2 = heading ? <m.h2 variants={variants} custom={0} className="rb-5 mb-5 text-2xl font-medium md:mb-6 md:text-4xl">{heading}</m.h2> : null;
    $[1] = heading;
    $[2] = variants;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = heading ? 1 : 0;
  let t4;
  if ($[4] !== body) {
    t4 = <LocationContentBody value={body} />;
    $[4] = body;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4 || $[8] !== variants) {
    t5 = <m.div variants={variants} custom={t3}>{t4}</m.div>;
    $[6] = t3;
    $[7] = t4;
    $[8] = variants;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t2 || $[11] !== t5) {
    t6 = <div className="self-start lg:sticky lg:top-20">{t2}{t5}</div>;
    $[10] = t2;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== heading || $[14] !== images || $[15] !== variants) {
    t7 = images.length > 0 ? <div className="grid grid-cols-1 gap-6 md:gap-8">{images.map((image, index) => <m.div key={`${image.src}-${index}`} variants={variants} custom={(heading ? 2 : 1) + index} className="relative aspect-[4/3] w-full overflow-hidden rounded-3xl"><Image src={image.src} alt={image.alt} fill={true} sizes="(max-width: 1023px) 100vw, 55vw" className="object-cover" /></m.div>)}</div> : null;
    $[13] = heading;
    $[14] = images;
    $[15] = variants;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <LazyMotion features={domAnimation}><m.section id="relume" variants={containerVariants} initial="hidden" whileInView="visible" viewport={t1} className="px-[5%] py-16 md:py-24 lg:py-28"><div className="container"><div className="grid grid-cols-1 items-start gap-x-12 gap-y-16 md:gap-y-20 lg:grid-cols-[1fr_1.5fr] lg:gap-x-20">{t6}{t7}</div></div></m.section></LazyMotion>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}